/**
 * Framework Tasks dashboard — layout aligned with chat shell (stone palette,
 * serif title, clean cards). Last-run excerpts stay collapsed by default so
 * long markdown reports do not flood the list.
 *
 * Contract: GET {apiBase} → { items: TaskItem[] }
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CalendarClock,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  ListTodo,
  Loader2,
  MessageSquare,
  RefreshCw,
} from 'lucide-react';
import { MarkdownRenderer } from '../components/MarkdownRenderer.js';
import { firstReportUrl } from '../lib/report-urls.js';

export interface TaskItem {
  id: string;
  title: string;
  status: string;
  subtitle?: string;
  next_run_at?: string;
  last_run_at?: string | null;
  meta?: string;
}

interface TasksPageProps {
  apiBase: string;
  title: string;
  navigate: (path: string) => void;
  /** Signed-in user slug — for markdown asset links in last-result previews. */
  viewerSlug: string;
}

function statusStyle(status: string): { dot: string; pill: string; label: string } {
  switch (status) {
    case 'active':
      return {
        dot: 'bg-emerald-500',
        pill: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
        label: 'Active',
      };
    case 'running':
      return {
        dot: 'bg-sky-500 animate-pulse',
        pill: 'bg-sky-50 text-sky-800 ring-sky-200',
        label: 'Running',
      };
    case 'paused':
      return {
        dot: 'bg-stone-400',
        pill: 'bg-stone-100 text-stone-600 ring-stone-200',
        label: 'Paused',
      };
    case 'completed':
      return {
        dot: 'bg-stone-300',
        pill: 'bg-stone-100 text-stone-600 ring-stone-200',
        label: 'Completed',
      };
    case 'cancelled':
      return {
        dot: 'bg-stone-300',
        pill: 'bg-stone-100 text-stone-500 ring-stone-200',
        label: 'Cancelled',
      };
    case 'failed':
    case 'disabled':
      return {
        dot: 'bg-rose-500',
        pill: 'bg-rose-50 text-rose-800 ring-rose-200',
        label: 'Failed',
      };
    default:
      return {
        dot: 'bg-amber-400',
        pill: 'bg-amber-50 text-amber-900 ring-amber-200',
        label: status,
      };
  }
}

function formatWhen(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

/** Flatten markdown-ish noise for a one-line list preview. */
function plainPreview(raw: string, max = 140): string {
  let s = raw
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/!\[[^\]]*]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]*)]\([^)]*\)/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^\|.*\|$/gm, ' ')
    .replace(/^[-*_]{3,}\s*$/gm, ' ')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
  if (s.length <= max) return s;
  return `${s.slice(0, max).trim()}…`;
}

function scheduleLabel(subtitle?: string): string {
  if (!subtitle) return 'Scheduled';
  const k = subtitle.toLowerCase();
  if (k === 'once') return 'One-time';
  if (k === 'daily') return 'Daily';
  if (k === 'weekly') return 'Weekly';
  return subtitle;
}

function TaskCard({ item, viewerSlug }: { item: TaskItem; viewerSlug: string }) {
  const [open, setOpen] = useState(false);
  const st = statusStyle(item.status);
  const next = formatWhen(item.next_run_at);
  const last = formatWhen(item.last_run_at);
  const meta = item.meta?.trim() || '';
  const isFail = item.status === 'failed' || item.status === 'disabled';
  const preview = meta ? plainPreview(meta) : null;
  // Only BinDrive /reports links — never the first news citation in the body.
  const link = meta ? firstReportUrl(meta) : null;
  const canExpand = Boolean(meta && meta.length > 0);

  return (
    <article className="rounded-2xl border border-stone-200/90 bg-white shadow-sm transition hover:border-stone-300 hover:shadow">
      <div className="flex gap-3 p-4 sm:p-5">
        <span
          className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${st.dot}`}
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <h2 className="font-serif text-base font-semibold tracking-tight text-stone-900 sm:text-[1.05rem]">
                {item.title}
              </h2>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${st.pill}`}
                >
                  {st.label}
                </span>
                <span className="inline-flex items-center rounded-full bg-stone-50 px-2 py-0.5 text-[11px] font-medium text-stone-600 ring-1 ring-inset ring-stone-200">
                  {scheduleLabel(item.subtitle)}
                </span>
              </div>
            </div>
            {link && (
              <a
                href={link}
                target="_blank"
                rel="noreferrer"
                className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-stone-200 bg-stone-50 px-2.5 py-1.5 text-[11px] font-medium text-stone-700 hover:bg-stone-100"
              >
                Open report <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>

          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-stone-500">
            {next && (
              <span className="inline-flex items-center gap-1">
                <CalendarClock className="h-3.5 w-3.5 text-stone-400" />
                <span className="text-stone-400">Next</span>
                <span className="font-medium text-stone-700">{next}</span>
              </span>
            )}
            {last && (
              <span className="inline-flex items-center gap-1">
                <span className="text-stone-400">Last</span>
                <span className="font-medium text-stone-700">{last}</span>
              </span>
            )}
            {!next && !last && (
              <span className="text-stone-400">No run times yet</span>
            )}
          </div>

          {canExpand && (
            <div className="mt-3 border-t border-stone-100 pt-3">
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-start gap-1.5 text-left"
              >
                {open ? (
                  <ChevronDown className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-400" />
                ) : (
                  <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-400" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-stone-400">
                    {isFail ? 'Last error' : 'Last result'}
                  </div>
                  {!open && preview && (
                    <p
                      className={`mt-0.5 line-clamp-2 text-sm leading-relaxed ${
                        isFail ? 'text-rose-700' : 'text-stone-600'
                      }`}
                    >
                      {preview}
                    </p>
                  )}
                </div>
              </button>
              {open && (
                <div
                  className={`mt-2 max-h-72 overflow-y-auto rounded-xl border px-3 py-2.5 text-sm leading-relaxed ${
                    isFail
                      ? 'border-rose-100 bg-rose-50/60 text-rose-900'
                      : 'border-stone-100 bg-stone-50/80 text-stone-800'
                  }`}
                >
                  {isFail ? (
                    <p className="whitespace-pre-wrap">{meta}</p>
                  ) : (
                    <div className="max-w-none text-sm [&_pre]:text-xs [&_table]:block [&_table]:w-full [&_table]:overflow-x-auto [&_table]:text-xs">
                      <MarkdownRenderer text={meta} viewerSlug={viewerSlug} />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

export function TasksPage({ apiBase, title, navigate, viewerSlug }: TasksPageProps) {
  const [items, setItems] = useState<TaskItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!apiBase) {
      setError('apiBase not configured for tasks page');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(apiBase, { credentials: 'include' });
      if (!res.ok) throw new Error(`Failed to load tasks (${res.status})`);
      const data = (await res.json()) as { items?: TaskItem[] };
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    void load();
  }, [load]);

  const sorted = useMemo(() => {
    const rank = (s: string) => {
      if (s === 'running') return 0;
      if (s === 'failed') return 1;
      if (s === 'active') return 2;
      if (s === 'paused') return 3;
      if (s === 'completed') return 4;
      return 5;
    };
    return [...items].sort((a, b) => {
      const d = rank(a.status) - rank(b.status);
      if (d !== 0) return d;
      const an = a.next_run_at ? Date.parse(a.next_run_at) : Number.POSITIVE_INFINITY;
      const bn = b.next_run_at ? Date.parse(b.next_run_at) : Number.POSITIVE_INFINITY;
      return an - bn;
    });
  }, [items]);

  const counts = useMemo(() => {
    let active = 0;
    let failed = 0;
    for (const it of items) {
      if (it.status === 'active' || it.status === 'running') active += 1;
      if (it.status === 'failed') failed += 1;
    }
    return { active, failed, total: items.length };
  }, [items]);

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-stone-50/80">
      {/* Page header — chat-adjacent stone chrome */}
      <div className="shrink-0 border-b border-stone-200/80 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <ListTodo className="h-4 w-4 text-stone-500" />
              <h1 className="font-serif text-lg font-semibold tracking-tight text-stone-900">
                {title}
              </h1>
            </div>
            <p className="mt-0.5 text-xs text-stone-500">
              {loading
                ? 'Loading…'
                : counts.total === 0
                  ? 'No scheduled work yet'
                  : `${counts.total} task${counts.total === 1 ? '' : 's'}${
                      counts.active ? ` · ${counts.active} active` : ''
                    }${counts.failed ? ` · ${counts.failed} failed` : ''}`}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-2.5 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-50"
            >
              <MessageSquare className="h-3.5 w-3.5" /> Chat
            </button>
            <button
              type="button"
              onClick={() => void load()}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-lg bg-stone-900 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-stone-800 disabled:opacity-60"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl px-4 py-5 sm:px-6 sm:py-6">
          <p className="mb-5 text-sm leading-relaxed text-stone-600">
            Create and manage tasks in chat — or use{' '}
            <code className="rounded-md bg-stone-200/70 px-1.5 py-0.5 text-[12px] text-stone-800">
              /tasks
            </code>{' '}
            on Telegram or Slack. Each run records usage against your account.
          </p>

          {error && (
            <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-800">
              {error}
            </div>
          )}

          {loading && items.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-2 py-20 text-stone-400">
              <Loader2 className="h-7 w-7 animate-spin" />
              <span className="text-sm">Loading tasks…</span>
            </div>
          )}

          {!loading && !error && items.length === 0 && (
            <div className="rounded-2xl border border-dashed border-stone-300 bg-white px-6 py-14 text-center shadow-sm">
              <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-stone-100">
                <ListTodo className="h-5 w-5 text-stone-500" />
              </div>
              <p className="font-serif text-base font-semibold text-stone-900">
                Nothing scheduled yet
              </p>
              <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-stone-500">
                Ask in chat — e.g. “Every Monday at 9am Asia/Singapore, summarize my
                portfolio.”
              </p>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-stone-900 px-3 py-2 text-xs font-medium text-white hover:bg-stone-800"
              >
                <MessageSquare className="h-3.5 w-3.5" /> Open chat
              </button>
            </div>
          )}

          {sorted.length > 0 && (
            <ul className="space-y-3">
              {sorted.map((it) => (
                <li key={it.id}>
                  <TaskCard item={it} viewerSlug={viewerSlug} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
